import React, { useMemo } from 'react';
import { Box, Typography, Paper, Tooltip, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StaffMember from '../../../../../components/common/StaffMember';
import ProfilePicture from '../../../../../components/common/ProfilePicture';
import { getStateConfig, getPriorityColor, getTaskColor, getTaskTextColor } from '../../../../tasks/utils/taskStateUtils';
import TaskCard from '../../../../tasks/components/TaskCard';

export default function TasksByUserView({ tasks, users = [], onSwitchUser }) {
  const { t } = useTranslation(['infoscreens', 'tasks']);
  const theme = useTheme();

  // Group tasks by primary assignee and include all users
  const tasksByUser = useMemo(() => {
    const tasksByUserId = {};
    const unassigned = [];

    // First, group tasks by user ID
    tasks.forEach(task => {
      if (task.primary_assignee) {
        const userId = task.primary_assignee.staff_member_id;
        if (!tasksByUserId[userId]) {
          tasksByUserId[userId] = [];
        }
        tasksByUserId[userId].push(task);
      } else {
        unassigned.push(task);
      }
    });

    // Create user groups for all users (including those without tasks)
    const userGroups = users.map(user => ({
      user: user,
      tasks: tasksByUserId[user.staff_member_id] || []
    }));

    // Sort tasks by title within each user group
    userGroups.forEach(userGroup => {
      userGroup.tasks.sort((a, b) => a.title.localeCompare(b.title));
    });

    // Sort unassigned tasks by title
    unassigned.sort((a, b) => a.title.localeCompare(b.title));

    return { sortedUsers: userGroups, unassigned };
  }, [tasks, users]);

  // Render individual task box
  const renderTaskBox = (task) => {
    const stateConfig = getStateConfig(task.state);
    const priorityColor = getPriorityColor(task.priority);
    const taskColor = getTaskColor(task);
    const textColor = getTaskTextColor(task);

    return (
      <Tooltip
        key={task.task_id}
        title={
          <TaskCard
            task={task}
            onEdit={() => {}}
            onDelete={() => {}}
            onView={() => {}}
            onStateChange={() => {}}
            canEdit={false}
            canDelete={false}
            canChangeState={false}
            hideContextMenu={true}
          />
        }
        placement="bottom-start"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: theme.shadows[4],
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              maxWidth: 500,
              p: 0,
            }
          },
          arrow: {
            sx: {
              color: 'background.paper',
              '&::before': {
                border: `1px solid ${theme.palette.divider}`,
              }
            }
          }
        }}
      >
        <Box
          onClick={() => onSwitchUser?.(`/tasks/${task.task_id}`)}
          sx={{
            position: 'relative',
            backgroundColor: taskColor,
            color: textColor,
            borderRadius: '22px',
            px: 5,
            py: 1.5,
            boxShadow: theme.shadows[2],
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            width: 400,
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            }
          }}
        >
          <Typography
            variant="body1"
            fontWeight="500"
            fontSize="1.1rem"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {task.title}
          </Typography>
          {task.started_by && (
            <Tooltip title={task.started_by.name} placement="top">
              <Box sx={{ position: 'absolute', top: -12, right: -12 }}>
                <ProfilePicture
                  name={task.started_by.name}
                  size={28}
                  imageSize="32x32"
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Tooltip>
    );
  };

  // Render user row
  const renderUserRow = (userGroup, index) => (
    <Box
      key={userGroup.user.staff_member_id}
      sx={{
        display: 'flex',
        borderBottom: '2px solid',
        borderColor: 'divider',
        bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.5),
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* User Info - Fixed width on left */}
      <Box
        sx={{
          minWidth: 200,
          maxWidth: 200,
          borderRight: "2px solid",
          borderColor: "divider",
          py: 2,
          px: 2
        }}
      >
        <StaffMember
          staffMember={userGroup.user}
          avatarSize={40}
          nameVariant="body1"
          clickable={false}
        />
      </Box>

      {/* Tasks - Flexible width on right */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-start',
        py: 2,
        px: 2
      }}>
        {userGroup.tasks.map(renderTaskBox)}
      </Box>
    </Box>
  );

  return (
    <Paper elevation={2} sx={{ overflow: 'hidden' }}>
      {/* Unassigned Tasks Section */}
      {tasksByUser.unassigned.length > 0 && (
        <Box sx={{
          display: 'flex',
          bgcolor: 'action.hover'
        }}>
          <Box sx={{
            minWidth: 200,
            maxWidth: 200,
            borderRight: "2px solid",
            borderColor: "divider",
            py: 2,
            px: 2
          }}>
            <Typography variant="body1" fontWeight="medium" color="text.secondary">
              {t('infoscreens:unassigned_tasks')}
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-start',
            py: 2,
            px: 2
          }}>
            {tasksByUser.unassigned.map(renderTaskBox)}
          </Box>
        </Box>
      )}

      {/* Assigned Tasks */}
      {tasksByUser.sortedUsers.map((userGroup, index) => renderUserRow(userGroup, index))}
    </Paper>
  );
}
